/**
 * Admin create/edit form for a single What's New entry.
 *
 * Uses the TipTap rich text editor for the description, with an "AI Generate"
 * button that calls Anthropic via the backend. Image upload uses the existing
 * /api/whats-new/admin/upload-image endpoint; uploaded files render as small
 * thumbnails with editable captions.
 */

import { Loader2, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

import {
  uploadImage,
  useCreateEntry,
  useGenerateDescription,
  usePermissionOptions,
  usePublishEntry,
  useUpdateEntry,
  type Category,
  type Entry,
  type EntryCreate,
  type EntryImage,
  type EntryLink,
  type EntryUpdate,
  type PermissionOptionGroup,
  type Visibility,
} from "../../api";

interface EntryFormProps {
  /** When set, edits this existing entry. When null, creates a new one. */
  initial: Entry | null;
  open: boolean;
  onClose: () => void;
}

function defaultPayload(): {
  title: string;
  category: Category;
  description: string;
  project: string;
  visibility: Visibility;
  required_permission: string;
  build_number: string;
  release_date: string;
  images: EntryImage[];
  links: EntryLink[];
} {
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return {
    title: "",
    category: "enhancement",
    description: "",
    project: "cinch_ops",
    visibility: "all",
    required_permission: "",
    build_number: "",
    release_date: iso,
    images: [],
    links: [],
  };
}

/**
 * Best guess at the gate for an entry, from the route it links to.
 *
 * The entry's first link is nearly always the feature it's about
 * (``/cash-flow`` → ``cash_flow.read``), so picking permission-gating can
 * pre-select the right answer instead of making someone hunt for it. Matches on
 * the feature segment rather than the whole route, since routes carry
 * sub-paths (``/cro-forecast/deal-health``) the permission key doesn't.
 */
function suggestPermission(
  links: EntryLink[],
  groups: PermissionOptionGroup[] | undefined,
): string {
  if (!groups) return "";
  const slug = links[0]?.url.split("/").filter(Boolean)[0];
  if (!slug) return "";
  const feature = slug.replace(/-/g, "_");
  const all = groups.flatMap((g) => g.options);
  return all.find((o) => o.feature === feature && o.action === "read")?.key ?? "";
}

export function EntryForm({ initial, open, onClose }: EntryFormProps) {
  const [form, setForm] = useState(() => defaultPayload());
  const [aiError, setAiError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const create = useCreateEntry();
  const update = useUpdateEntry();
  const publish = usePublishEntry();
  const generate = useGenerateDescription();
  // Only fetched while the dialog is open — it's static config behind an admin
  // permission, not something to pull on every admin-page render.
  const permissionOptions = usePermissionOptions(open);

  // Reset whenever opened (or which entry we're editing changes).
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        title: initial.title,
        category: initial.category,
        description: initial.description,
        project: initial.project,
        visibility: initial.visibility,
        required_permission: initial.required_permission ?? "",
        build_number: initial.build_number ?? "",
        release_date: initial.release_date,
        images: initial.images ?? [],
        links: initial.links ?? [],
      });
    } else {
      setForm(defaultPayload());
    }
    setAiError(null);
    setUploadError(null);
  }, [open, initial]);

  const isPublished = initial?.status === "published";

  const payload = useMemo<EntryCreate>(
    () => ({
      title: form.title.trim(),
      category: form.category,
      description: form.description,
      project: form.project.trim(),
      visibility: form.visibility,
      // Only ever sent on the permission tier — the API rejects a key on any
      // other tier, and null is what clears a previously-gated entry.
      required_permission:
        form.visibility === "permission" ? form.required_permission : null,
      images: form.images.length ? form.images : null,
      links: form.links.length ? form.links : null,
      build_number: form.build_number.trim() || null,
      release_date: form.release_date,
    }),
    [form],
  );

  const save = async (alsoPublish: boolean): Promise<void> => {
    let id = initial?.id ?? null;
    if (initial) {
      const body: EntryUpdate = { ...payload };
      await update.mutateAsync({ entryId: initial.id, body });
    } else {
      const created = await create.mutateAsync(payload);
      id = created.id;
    }
    if (alsoPublish && id) {
      await publish.mutateAsync(id);
    }
    onClose();
  };

  const runAiGenerate = async () => {
    setAiError(null);
    try {
      const res = await generate.mutateAsync({
        title: form.title,
        category: form.category,
        project: form.project,
        build_number: form.build_number || null,
        git_commit_hash: initial?.git_commit_hash ?? null,
        existing_description: form.description || null,
      });
      // Wrap the markdown-y response in <p> tags so TipTap renders it. The
      // editor will normalize on first edit.
      const html = res.description
        .split(/\n\n+/)
        .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br/>")}</p>`)
        .join("");
      setForm((f) => ({ ...f, description: html }));
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "AI generation failed");
    }
  };

  const onUpload = async (file: File) => {
    setUploadError(null);
    try {
      const { url } = await uploadImage(file);
      setForm((f) => ({
        ...f,
        images: [...f.images, { url, caption: "" }],
      }));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    }
  };

  const saving = create.isPending || update.isPending || publish.isPending;
  const titleOk = form.title.trim().length > 0;
  // The API rejects visibility="permission" with no key (422); catching it here
  // keeps that from surfacing as a raw validation error on save.
  const gateOk =
    form.visibility !== "permission" || form.required_permission.length > 0;
  const canSave = titleOk && gateOk;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit entry" : "New entry"}</DialogTitle>
          <DialogDescription>
            Drafts are saved without publishing. Use Save &amp; Publish to ship to users.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="entry-title">Title</Label>
            <Input
              id="entry-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Short, user-facing headline"
              className="mt-1"
            />
          </div>

          <FieldSelect
            id="entry-category"
            label="Category"
            value={form.category}
            onChange={(v) => setForm((f) => ({ ...f, category: v as Category }))}
            options={[
              { value: "new", label: "New" },
              { value: "enhancement", label: "Enhancement" },
              { value: "fix", label: "Fix" },
            ]}
          />

          <div>
            <Label htmlFor="entry-project">Project</Label>
            <Input
              id="entry-project"
              value={form.project}
              onChange={(e) => setForm((f) => ({ ...f, project: e.target.value }))}
              className="mt-1"
            />
          </div>

          <FieldSelect
            id="entry-visibility"
            label="Visibility"
            value={form.visibility}
            onChange={(v) =>
              setForm((f) => {
                const visibility = v as Visibility;
                return {
                  ...f,
                  visibility,
                  // Pre-select the gate from the entry's own link so the common
                  // case is one click, and drop it again on the way out.
                  required_permission:
                    visibility === "permission"
                      ? f.required_permission ||
                        suggestPermission(f.links, permissionOptions.data)
                      : "",
                };
              })
            }
            options={[
              { value: "all", label: "All users" },
              { value: "internal", label: "Internal (@cinch.io)" },
              { value: "admin", label: "Admin only" },
              { value: "permission", label: "Anyone with feature access" },
            ]}
          />

          {form.visibility === "permission" && (
            <div className="sm:col-span-2">
              <Label htmlFor="entry-permission">Required permission</Label>
              <select
                id="entry-permission"
                value={form.required_permission}
                onChange={(e) =>
                  setForm((f) => ({ ...f, required_permission: e.target.value }))
                }
                className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Select a permission…</option>
                {(permissionOptions.data ?? []).map((group) => (
                  <optgroup key={group.category} label={group.category}>
                    {group.options.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.key} — {o.description}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {permissionOptions.isError
                  ? "Couldn't load permissions — pick another visibility, or reload."
                  : "Only people who hold this permission (and an @cinch.io address) will see the entry — in the timeline and in the unread count."}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="entry-build">Build / version</Label>
            <Input
              id="entry-build"
              value={form.build_number}
              onChange={(e) => setForm((f) => ({ ...f, build_number: e.target.value }))}
              placeholder="optional"
              className="mt-1"
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="entry-date">Release date</Label>
            <Input
              id="entry-date"
              type="date"
              value={form.release_date}
              onChange={(e) => setForm((f) => ({ ...f, release_date: e.target.value }))}
              className="mt-1"
            />
          </div>

          <div className="sm:col-span-2">
            <div className="mb-1 flex items-center justify-between">
              <Label htmlFor="entry-desc">Description</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={runAiGenerate}
                disabled={generate.isPending || !form.title}
              >
                {generate.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {form.description ? "AI rewrite" : "AI generate"}
              </Button>
            </div>
            <RichTextEditor
              id="entry-desc"
              value={form.description}
              onChange={(html) => setForm((f) => ({ ...f, description: html }))}
              placeholder="Describe what changed and why it matters…"
            />
            {aiError && (
              <p className="mt-1 text-xs text-destructive">{aiError}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label>Images</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {form.images.map((img, i) => (
                <div
                  key={i}
                  className="group relative overflow-hidden rounded-md border border-border"
                >
                  <img
                    src={img.url}
                    alt={img.caption || ""}
                    className="block h-20 w-auto object-cover"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        images: f.images.filter((_, j) => j !== i),
                      }))
                    }
                    className="absolute right-1 top-1 rounded-full bg-black/50 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label="Remove image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <Input
                    value={img.caption ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        images: f.images.map((it, j) =>
                          j === i ? { ...it, caption: e.target.value } : it,
                        ),
                      }))
                    }
                    placeholder="Caption (optional)"
                    className="h-7 rounded-none border-0 border-t text-xs"
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus className="h-3.5 w-3.5" />
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void onUpload(file);
                  e.target.value = "";
                }}
              />
            </div>
            {uploadError && (
              <p className="mt-1 text-xs text-destructive">{uploadError}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label>Links</Label>
            <div className="mt-1 flex flex-col gap-2">
              {form.links.map((link, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={link.label}
                    placeholder="Label"
                    className="flex-1"
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        links: f.links.map((l, j) =>
                          j === i ? { ...l, label: e.target.value } : l,
                        ),
                      }))
                    }
                  />
                  <Input
                    value={link.url}
                    placeholder="https://…"
                    className="flex-[2]"
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        links: f.links.map((l, j) =>
                          j === i ? { ...l, url: e.target.value } : l,
                        ),
                      }))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        links: f.links.filter((_, j) => j !== i),
                      }))
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    links: [...f.links, { label: "", url: "" }],
                  }))
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Add link
              </Button>
            </div>
          </div>
        </div>

        <footer className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => save(false)}
            disabled={!canSave || saving}
          >
            Save as draft
          </Button>
          {!isPublished && (
            <Button onClick={() => save(true)} disabled={!canSave || saving}>
              Save &amp; publish
            </Button>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function FieldSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
